// src/modules/units/tyres/views/retread-batch/retreadbatchproduct.view.js
//
// Shared rendering for the retread product a batch is tied to. The product fields
// are denormalised onto the batch by the backend (manufacturerName, modelName,
// treadPatternCode, tyreApplication, and the numeric specs), so this reads straight
// off the batch — no catalogue lookup. Both the batch detail and the receive screen
// use it, so they show the product identically. Old batches created before the
// catalogue link carry nulls; hasProduct is then false and nothing renders.
//
// Numbers go through the house formatters (formatTread -> "18 mm", formatKm ->
// "120 000 km" with a space thousands separator), so the spec matches tread and
// mileage everywhere else in the app.

import { esc, formatTread, formatKm } from '../../shared/tyre-format.js';

export function retreadProductBits(batch = {}) {
  const hasProduct = !!(batch.tyreCatalogueEntryId || batch.manufacturerName);
  // Brand and model are middot-joined (matching the picker's catalogueLabel and the
  // sub below), so "Bandag" + "bd m" reads "Bandag · bd m", not "Bandag bd m".
  const name = [batch.manufacturerName, batch.modelName].filter(Boolean).join(' · ').trim();
  const sub = [batch.treadPatternCode, batch.tyreApplication].filter(Boolean).join(' · ');

  const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
  const nominal = num(batch.manufacturerNominalTreadDepth);
  const minRetread = num(batch.minimumTreadForRetread);
  const mileage = num(batch.manufacturerExpectedMileage);
  const spec = [
    Number.isFinite(nominal) ? `${formatTread(nominal)} nominal` : null,
    Number.isFinite(minRetread) ? `min ${formatTread(minRetread)}` : null,
    Number.isFinite(mileage) ? formatKm(mileage) : null
  ].filter(Boolean).join(' · ');

  return { hasProduct, name, sub, spec };
}

// A standalone facts block (Retread product + Retread spec). Used on the receive
// screen; the detail folds the same bits into its own facts grid. '' when there is
// no product (old batches).
export function retreadProductFactsHTML(batch = {}) {
  const { hasProduct, name, sub, spec } = retreadProductBits(batch);
  if (!hasProduct) return '';
  const fact = (label, value, subText = '') => `
    <div class="tm-fact">
      <span class="tm-fact-label">${esc(label)}</span>
      <span class="tm-fact-value">${value}${subText ? `<span class="tm-fact-sub"> · ${esc(subText)}</span>` : ''}</span>
    </div>`;
  const rows = [fact('Retread product', esc(name || '—'), sub)];
  if (spec) rows.push(fact('Retread spec', esc(spec)));
  return `<div class="tm-facts tm-rb-recv-product">${rows.join('')}</div>`;
}
